/**
 * Logs utility functions for agent testing
 */

/**
 * Safe render utility to ensure objects are never passed directly to React
 */
export const safeRenderValue = (value) => {
  if (value === null || value === undefined) {
    return 'No data available';
  }
  
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch (e) {
      return '[Object - Cannot display]';
    }
  }
  
  if (typeof value === 'string') {
    return value;
  }
  
  return String(value);
};

/**
 * Extract data from nested response structures
 */
export const extractFromNestedResponse = (response, ...keys) => {
  if (!response) return null;
  
  console.log('Extracting from nested response structure:', Object.keys(response));
  
  if (response.body?.data?.test_results?.endpoint_response?.body) {
    try {
      console.log('Found test_results structure with string body, parsing...');
      const parsedBody = typeof response.body.data.test_results.endpoint_response.body === 'string' 
        ? JSON.parse(response.body.data.test_results.endpoint_response.body)
        : response.body.data.test_results.endpoint_response.body;
      
      console.log('Successfully parsed test_results body:', parsedBody);
      
      let value = parsedBody;
      for (const key of keys) {
        if (value && typeof value === 'object' && key in value) {
          value = value[key];
        } else {
          return null;
        }
      }
      return value;
    } catch (parseError) {
      console.warn('Failed to parse test_results body:', parseError);
    }
  }
  
  if (response.data?.test_results?.endpoint_response?.body) {
    try {
      console.log('Found test_results structure in data, parsing...');
      const parsedBody = typeof response.data.test_results.endpoint_response.body === 'string' 
        ? JSON.parse(response.data.test_results.endpoint_response.body)
        : response.data.test_results.endpoint_response.body;
      
      console.log('Successfully parsed test_results body from data:', parsedBody);
      
      let value = parsedBody;
      for (const key of keys) {
        if (value && typeof value === 'object' && key in value) {
          value = value[key];
        } else {
          return null;
        }
      }
      return value;
    } catch (parseError) {
      console.warn('Failed to parse test_results body from data:', parseError);
    }
  }
  
  let value = response;
  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      return null;
    }
  }
  return value;
};

/**
 * Parse live logs output
 */
export const parseLiveLogsOutput = (logs) => {
  try {
    console.log('Parsing live logs for output fields:', logs);
    
    let executionSummary = null;
    let agentOutput = null;
    
    if (logs && typeof logs === 'object') {
      if (logs.output && logs.output.agentOutput && logs.output.executionSummary) {
        agentOutput = logs.output.agentOutput;
        executionSummary = logs.output.executionSummary;
      }
      else if (logs.Output && logs.Output.agentOutput && logs.Output.executionSummary) {
        agentOutput = logs.Output.agentOutput;
        executionSummary = logs.Output.executionSummary;
      }
      else if (logs.body && typeof logs.body === 'string') {
        try {
          const parsedBody = JSON.parse(logs.body);
          
          if (parsedBody.output && parsedBody.output.agentOutput && parsedBody.output.executionSummary) {
            agentOutput = parsedBody.output.agentOutput;
            executionSummary = parsedBody.output.executionSummary;
          }
          else if (parsedBody.Output && parsedBody.Output.agentOutput && parsedBody.Output.executionSummary) {
            agentOutput = parsedBody.Output.agentOutput;
            executionSummary = parsedBody.Output.executionSummary;
          }
        } catch (parseError) {
          console.warn('Failed to parse body as JSON:', parseError);
        }
      }
      else if (logs.body && typeof logs.body === 'object') {
        if (logs.body.output && logs.body.output.agentOutput && logs.body.output.executionSummary) {
          agentOutput = logs.body.output.agentOutput;
          executionSummary = logs.body.output.executionSummary;
        }
        else if (logs.body.Output && logs.body.Output.agentOutput && logs.body.Output.executionSummary) {
          agentOutput = logs.body.Output.agentOutput;
          executionSummary = logs.body.Output.executionSummary;
        }
      }
      else if (logs.executionSummary || logs.agentOutput) {
        executionSummary = logs.executionSummary;
        agentOutput = logs.agentOutput;
      }
      else if (logs.lambdaSteps && Array.isArray(logs.lambdaSteps)) {
        for (const step of logs.lambdaSteps) {
          if (step.status === 'COMPLETED' && step.details && step.details.result) {
            if (step.details.result.output && step.details.result.output.agentOutput && step.details.result.output.executionSummary) {
              agentOutput = step.details.result.output.agentOutput;
              executionSummary = step.details.result.output.executionSummary;
              break;
            }
          }
        }
      }
    }
    
    return {
      executionSummary: executionSummary !== null ? safeRenderValue(executionSummary) : null,
      agentOutput: agentOutput !== null ? safeRenderValue(agentOutput) : null
    };
    
  } catch (error) {
    console.error('Error parsing live logs output:', error);
    return {
      executionSummary: 'Error parsing execution summary',
      agentOutput: 'Error parsing agent output'
    };
  }
};

/**
 * Format live logs response
 */
export const formatLiveLogsResponse = (response) => {
  if (!response) return null;
  
  try {
    const completeLog = {
      ...response,
      lastUpdated: new Date().toLocaleTimeString()
    };
    
    return completeLog;
  } catch (error) {
    console.error('Error formatting logs response:', error);
    return response;
  }
};

/**
 * Get status badge variant based on status
 */
export const getStatusBadgeVariant = (status) => {
  if (!status) return 'secondary';
  
  if (typeof status === 'object' && status.status) {
    status = status.status;
  }
  
  if (typeof status === 'string') {
    status = status.toUpperCase();
    
    if (status === 'RUNNING' || status === 'IN_PROGRESS') return 'primary';
    if (status === 'SUCCEEDED') return 'success';
    if (status === 'FAILED') return 'danger';
    if (status === 'TIMED_OUT') return 'warning';
    if (status === 'ABORTED') return 'warning';
  }
  
  return 'secondary';
};

/**
 * Format execution duration
 */
export const formatDuration = (startDate, stopDate) => {
  if (!startDate || !stopDate) return 'In progress';
  
  try {
    const start = new Date(startDate);
    const stop = new Date(stopDate);
    const durationMs = stop - start;
    
    if (durationMs >= 60000) {
      const minutes = Math.floor(durationMs / 60000);
      const seconds = Math.floor((durationMs % 60000) / 1000);
      return `${minutes}m ${seconds}s`;
    }
    
    const seconds = Math.floor(durationMs / 1000);
    return `${seconds}s`;
  } catch (e) {
    return 'Unknown';
  }
};
